<template>
  <section id="section-itinerary">
    <div class="title">
      <template v-if="!itinerary.meta.hasOverrideCountry">
        <a :href="itinerary.meta.productCountryUrl">{{ itinerary.targetCountryName }}</a>
        <span> &gt; </span>
      </template>
      <span>{{ itinerary.title }}</span>
    </div>

    <template v-if="itinerary.preludeHtml">
      <div v-if="!editable" class="itinerary-prelude" v-html="itinerary.preludeHtml"></div>
      <EditableText
        v-else-if="editable"
        class="editable-itinerary-prelude"
        v-model="highlight"
        html
        :max-length="2000"
        @onBlur="updatePrelude"
      />
    </template>

    <TripEditContactUs v-if="editable" class="contact-us" @click="handleClick" />

    <SectionHeading
      id="toc-itinerary"
      class="heading"
      icon="icon-trip-destination"
      title="Itinerary timeline"
      v-if="shouldShowTimeline(itinerary.days)"
    />

    <ItineraryTimeline class="timeline" :startsAt="tripStartsAt" :days="daysWithTimeline(itinerary.days)" />

    <SectionHeading class="heading" icon="icon-travel-crossroad-direction-board" title="Details" />

    <ItineraryTimelineDetails class="details" :startsAt="tripStartsAt" :days="itinerary.days" />
  </section>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import SectionHeading from '@/modules/Itinerary/Components/SectionHeading.vue'
import ItineraryTimeline from '@/modules/Itinerary/Components/ItineraryTimeline.vue'
import ItineraryTimelineDetails from '@/modules/Itinerary/Components/ItineraryTimelineDetails.vue'
import EditableText from '@/components/EditableText.vue'
import { TripEditContactUs } from '@/modules/SelfServePublish'
import { useTypedItineraryStore } from '../../stores/typed'
import { Product } from '../../models'
import { usePublishSetupStore } from '../../stores/publishSetup'

defineProps<{
  editable: boolean
}>()

const store = useTypedItineraryStore<Product>()
const {
  data: { itinerary, tripStartsAt },
} = store
const publishSetupStore = usePublishSetupStore()

store.monitorVisibility('itinerary', '#section-itinerary')

const highlight = ref(itinerary?.preludeHtml ?? '')

const daysWithTimeline = days => {
  return days.filter(day => {
    return day.activities.some(
      activity => activity.type === 'transportation' || activity.description?.trim() || activity.shortTitle?.trim(),
    )
  })
}

const shouldShowTimeline = days => daysWithTimeline(days).length > 0

const handleClick = () => {
  publishSetupStore.setIsFeedbackOpen(true)
}

const updatePrelude = async (props: { field: 'title' | 'prelude'; value: string }) => {
  if (props.field !== 'prelude' && props.value.length > 2000) {
    return
  }
  await store.updateProduct({ productId: itinerary.id, prelude: props.value })
}
</script>

<style lang="scss" scoped>
.title {
  margin-bottom: 32px;

  * {
    color: $grey-100;
  }
}

.itinerary-prelude {
  border-radius: 15px;
  background: $grey-25;
  padding: 24px;
  margin-bottom: 24px;

  &:deep {
    @include markdown-display;
  }
}

.contact-us {
  margin-bottom: 24px;
}

.editable-itinerary-prelude {
  width: fit-content;
  border-radius: 15px;
  background: $grey-25;
  margin-bottom: 24px;

  &:deep {
    @include markdown-display;
  }
}

.heading {
  margin-bottom: 24px;
}

.timeline {
  margin-bottom: 32px;

  @include mobile {
    margin-bottom: 48px;
  }
}

@include mobile {
  .title,
  .itinerary-prelude,
  .heading {
    margin-bottom: 16px;
  }
}
</style>
